import { useState } from 'react';
import { Box, Chip, Collapse, IconButton, Tooltip, Typography } from '@mui/material';
import {
    BugReport as BugReportIcon,
    Science as ScienceIcon,
    Rocket as RocketIcon,
    Close as CloseIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {useLocation} from "react-router-dom";

const ENV_CONFIG = {
    development: {
        label: 'DESENVOLVIMENTO',
        shortLabel: 'DEV',
        icon: BugReportIcon,
        bg: 'repeating-linear-gradient(45deg, #1a1a2e 0px, #1a1a2e 10px, #16213e 10px, #16213e 20px)',
        accent: '#00d4ff',
        accentDark: '#0099bb',
        textColor: '#00d4ff',
        pulse: true,
        description: 'Ambiente de desenvolvimento local',
    },
    homolog: {
        label: 'HOMOLOGAÇÃO',
        shortLabel: 'HML',
        icon: ScienceIcon,
        bg: 'repeating-linear-gradient(45deg, #2d1b00 0px, #2d1b00 10px, #1a1000 10px, #1a1000 20px)',
        accent: '#ff9500',
        accentDark: '#cc7700',
        textColor: '#ffb347',
        pulse: true,
        description: 'Ambiente de testes e validação',
    },
    production: {
        label: 'PRODUÇÃO',
        shortLabel: 'PROD',
        icon: RocketIcon,
        bg: 'linear-gradient(135deg, #0a2e0a 0%, #0d1a0d 100%)',
        accent: '#00e676',
        accentDark: '#00b85a',
        textColor: '#69f0ae',
        pulse: false,
        description: 'Ambiente de produção',
    },
};

function detectEnvironment() {
    const api = process.env.REACT_APP_API || '';

    if (api.includes('private-api') || api.includes('api.safetowork')) return 'production';
    if (api.includes('api-asw-teste')) return 'homolog';
    if (api.includes('api-teste')) return 'development';

    return 'development';
}

export function EnvironmentBanner({ isSempher }) {
    const location = useLocation();

    const [visible, setVisible] = useState(true);
    const [collapsed, setCollapsed] = useState(false);

    if (!isSempher) return null;

    const envKey = detectEnvironment();
    const config = ENV_CONFIG[envKey];
    const Icon = config.icon;

    // if (envKey === 'production') return null;
    if (location.pathname === '/monitor-dashboard') return null;

    if (!visible) return null;

    return (
        <Box
            sx={{
                position: 'fixed',
                bottom: 16,
                right: 16,
                zIndex: 9999,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 0,
                filter: 'drop-shadow(0 8px 32px rgba(0,0,0,0.5))',
            }}
        >
            <Box
                onClick={() => setCollapsed(c => !c)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 0.75,
                    borderRadius: collapsed ? '20px' : '12px 12px 0 0',
                    background: config.bg,
                    border: `1.5px solid ${config.accent}`,
                    borderBottom: collapsed ? `1.5px solid ${config.accent}` : 'none',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        filter: `brightness(1.15)`,
                    },
                }}
            >
                {config.pulse && (
                    <Box sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: config.accent,
                        flexShrink: 0,
                        '@keyframes pulseRing': {
                            '0%': { boxShadow: `0 0 0 0 ${config.accent}88` },
                            '70%': { boxShadow: `0 0 0 6px ${config.accent}00` },
                            '100%': { boxShadow: `0 0 0 0 ${config.accent}00` },
                        },
                        animation: 'pulseRing 1.5s ease-out infinite',
                    }} />
                )}

                <Icon sx={{ color: config.textColor, fontSize: 16 }} />

                <Typography
                    sx={{
                        color: config.textColor,
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        letterSpacing: '0.15em',
                    }}
                >
                    {config.shortLabel}
                </Typography>

                {collapsed
                    ? <ExpandLessIcon sx={{ color: config.textColor, fontSize: 14 }} />
                    : <ExpandMoreIcon sx={{ color: config.textColor, fontSize: 14 }} />
                }
            </Box>

            <Collapse in={!collapsed} unmountOnExit>
                <Box
                    sx={{
                        background: config.bg,
                        border: `1.5px solid ${config.accent}`,
                        borderTop: 'none',
                        borderRadius: '0 0 12px 12px',
                        p: 2,
                        minWidth: 240,
                        position: 'relative',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Icon sx={{ color: config.accent, fontSize: 22 }} />
                        <Typography
                            sx={{
                                color: config.textColor,
                                fontFamily: 'monospace',
                                fontWeight: 800,
                                fontSize: '0.85rem',
                                letterSpacing: '0.2em',
                            }}
                        >
                            {config.label}
                        </Typography>
                    </Box>

                    <Box sx={{ height: '1px', bgcolor: `${config.accent}44`, mb: 1.5 }} />

                    <Typography
                        sx={{
                            color: `${config.textColor}bb`,
                            fontSize: '0.72rem',
                            fontFamily: 'monospace',
                            mb: 1.5,
                        }}
                    >
                        {config.description}
                    </Typography>

                    <Box
                        sx={{
                            bgcolor: 'rgba(0,0,0,0.4)',
                            borderRadius: 1,
                            px: 1.25,
                            py: 0.75,
                            border: `1px solid ${config.accent}33`,
                        }}
                    >
                        <Typography sx={{ color: `${config.textColor}88`, fontSize: '0.6rem', fontFamily: 'monospace', mb: 0.25 }}>
                            API
                        </Typography>
                        <Typography
                            sx={{
                                color: config.textColor,
                                fontSize: '0.65rem',
                                fontFamily: 'monospace',
                                wordBreak: 'break-all',
                            }}
                        >
                            {process.env.REACT_APP_API || 'não definida'}
                        </Typography>
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
}